import { Journey } from '@audibene/ta.utils-opportunity'
import { Alert, Button, Grid, SelectChangeEvent, Typography } from '@mui/material'
import { addDays, endOfMonth } from 'date-fns'
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  ANY_RESOURCE_ID,
  ANY_INTERNAL_RESOURCE_ID,
  ANY_EXTERNAL_RESOURCE_ID,
  AppointmentType,
  AvailabilityMode,
  Country,
  ResourceType,
  TeamAccess,
  teleaudiologyOrTaLightJourneys,
} from '../../../../constants'

import {
  useAcfuProgressiveDisclosure,
  useAppointmentTypeOptions,
  useAsegoAvailabilityDays,
  useAsegoTimesIntegration,
  useBlockedAppointmentType,
  useCombinedResourceAvailabilities,
  useCountry,
  useDebug,
  useEventTracking,
  useFeatureFlags,
  useNow,
  useRescheduleContext,
  useSearchParameters,
  useShouldRestrictToHAE,
  useShopIdToIsCabinOnly,
} from '../../../../hooks'
import { useTrialGateTracking } from '../../../../hooks/useTrialGateTracking'
import {
  AppointmentTypeSubstitution,
  getSubstituteAppointmentType,
  getTrialGate,
  TrialGateSurface,
} from '../../../../hooks/useTrialGate/trialGates'
import { useProviderRecommendations } from '../../../../hooks/useProviderRecommendations'

import { AppointmentFormData, Customer, EventData, Resource, TimeZones, toResource } from '../../../../types'
import {
  appointmentToTitle,
  createTimeZonesFromGroups,
  getTimeZoneOptions,
  isAnyResourceId,
  resourceTypeToDisplayText,
  sortByGroupingThenName,
  startOfDayTimezoned,
  toGatedAppointmentTypeOptions,
} from '../../../../util'
import { getMinimumAllowedDateUtc } from '../../../../util/getMinimumAllowedDate'

import { useConsultantUser } from 'hooks/useConsultantUser'
import { mergeAvailabilities } from '../../../../util/mergeAvailabilities'
import { mergeAvailabilityResponses } from '../../../../util/mergeAvailabilityResponses'
import { Select, TextArea } from '../../../forms'
import { determineRecommendationIssues, Issues } from '../../../Issues'
import { Loading } from '../../../Reservation/Common/Loading'
import { ScheduleContent } from '../../../ScheduleContent'
import { AcfuExtendedWindowReason } from '../../AcfuExtendedWindowReason'
import { AcfuShowMoreDatesButton } from '../../AcfuShowMoreDatesButton'
import { AppointmentTypeSubstitutedAlert } from '../../AppointmentTypeSubstitutedAlert'
import { ActionButton } from './ActionButton'
import { Day } from './Day'
import { Time } from './Time'
import { useManualTimeEnabledResource } from './useManualTimeEnabledResource'
import { useRedirectToConsultationSchedulingFlowIfNeeded } from './useRedirectToConsultationSchedulingFlowIfNeeded'
import { filterResource } from 'util/filterResources'
import { filterByResource } from 'util/filterByResource'

type ScheduleFormProps = {
  customer: Customer
  journey: Journey
  timeZones: TimeZones
  trackingData: EventData
  /**
   * Set by the route when it defaulted the form to something other than what was asked for, so the
   * note reports a substitution that actually happened rather than one inferred from the URL.
   * Inference got this wrong both ways: silent about a mid-session steer, and claiming a
   * substitution on the type-selection path, where the user picked the type themselves.
   */
  appointmentTypeSubstitution?: AppointmentTypeSubstitution
}

const ScheduleForm = ({ customer, journey, timeZones, trackingData, appointmentTypeSubstitution }: ScheduleFormProps) => {
  const { setValue, watch, formState, getValues } = useFormContext<AppointmentFormData>()
  const debug = useDebug()

  const [type, customer_timezone, monthDate, clientStartDate, specialistId, teamAccess] = watch([
    'type',
    'customer_timezone',
    'month_date',
    'clientStartDate',
    'specialist_id',
    'teamAccess',
  ])

  const country = useCountry()
  const rescheduleContext = useRescheduleContext()
  const { t } = useTranslation()
  const isRescheduling = Boolean(rescheduleContext?.reason)
  const shouldRestrictToHAE = useShouldRestrictToHAE(journey)

  const searchParameters = useSearchParameters()
  const restrictToShopSalesforceId = searchParameters.restrict_to_shop
  if (Array.isArray(restrictToShopSalesforceId)) throw new Error('restrict_to_shop search parameter should not be an array')

  const { appointmentTypes, isLoading: areAppointmentTypesLoading } = useAppointmentTypeOptions({
    country,
    journey,
  })

  const {
    activeTrialGateTypes,
    blockedAppointmentTypes,
    isTrialDataUnknown,
    reasons: blockedAppointmentTypeReasons,
    trialEndDate,
    trialStatusSource,
  } = useBlockedAppointmentType()
  const { track: trackEvent } = useEventTracking()

  const appointmentTypeOptions = useMemo(
    () =>
      toGatedAppointmentTypeOptions({
        appointmentTypes,
        blockedAppointmentTypes,
        reasons: blockedAppointmentTypeReasons,
        shouldRestrictToHAE,
      }),
    [appointmentTypes, blockedAppointmentTypes, blockedAppointmentTypeReasons, shouldRestrictToHAE]
  )

  // Taken from the rendered options rather than re-deriving the conditions, so it cannot drift from
  // what the user is shown: the options drop a type both when shouldRestrictToHAE filters to HAE
  // only, and when the shop's appointment-type config simply does not include it.
  const offeredAppointmentTypes = useMemo(() => appointmentTypeOptions.map((option) => option.value), [appointmentTypeOptions])

  useTrialGateTracking({
    activeTrialGateTypes,
    blockedAppointmentTypes,
    isTrialDataUnknown,
    offeredAppointmentTypes,
    surface: TrialGateSurface.SCHEDULE_FORM,
    trackEvent,
    trialEndDate,
    trialStatusSource,
  })

  // Disabling the option is a UI constraint; the form's value was chosen at mount. If the blocked set
  // grows while the page is open - LaunchDarkly streams the flag on, or useNow crosses the trial
  // boundary - the field would sit on a greyed-out value that still submits, since the schema only
  // checks enum membership. Steer it to the same replacement the defaults would have picked, and
  // clear the slot with it: the time already chosen was picked against the old type's availability
  // and duration, neither of which carries over. Never while rescheduling.
  const [substitution, setSubstitution] = useState(appointmentTypeSubstitution)
  const liveTypeSubstitute = getSubstituteAppointmentType(type as AppointmentType, blockedAppointmentTypes)
  const isLiveTypeSubstituteOffered = appointmentTypeOptions.some((option) => option.value === liveTypeSubstitute)

  // Sticky for the same reason schedule/New keeps its own copy sticky: onSubmitClick clears the
  // reschedule context on success and only then navigates, so there is a commit where this reads
  // false while `type` still holds the rescheduled appointment's own type. Reading it live there
  // would rewrite that type and wipe the slot while the success handler is still running.
  const [isReschedulingSession, setIsReschedulingSession] = useState(isRescheduling)
  if (isRescheduling && !isReschedulingSession) setIsReschedulingSession(true)

  useEffect(() => {
    if (isReschedulingSession || !liveTypeSubstitute || !isLiveTypeSubstituteOffered) return
    // Read through getValues rather than the watched `type`: `type` is what this effect writes, so
    // depending on it would re-run on its own write and record a substitution from the value just
    // substituted in.
    setSubstitution({ from: getValues('type') as AppointmentType, to: liveTypeSubstitute })
    setValue('type', liveTypeSubstitute, { shouldDirty: true, shouldValidate: true })
    setValue('clientStartTime', null)
    setValue('start_date', null)
    // Mirrors onResourceChange below rather than always clearing: specialist_id is untouched here,
    // and the two have to agree - "anyone" is carried as a placeholder id, not an empty list.
    setValue('resource_ids', isAnyResourceId(getValues('specialist_id') as string) ? [ANY_RESOURCE_ID] : [])
  }, [getValues, isLiveTypeSubstituteOffered, isReschedulingSession, liveTypeSubstitute, setValue])

  // This route substitutes in two places neither of which is useScheduleFormDefaultValues - the
  // mount-time default in schedule/New, and the live steer above - so without this the applied-
  // substitution event would miss exactly the flow PX-3749 adds and the event would read as dead.
  // Keyed on the type substituted away from, so a second, genuinely different substitution within
  // one mount still reports.
  const trackEventRef = useRef(trackEvent)
  trackEventRef.current = trackEvent
  const substitutedFromType = substitution?.from

  useEffect(() => {
    if (!substitutedFromType) return

    const substitutionAppliedEvent = getTrialGate(substitutedFromType)?.substitutionAppliedEvent
    if (!substitutionAppliedEvent) return

    trackEventRef.current(substitutionAppliedEvent)
  }, [substitutedFromType])

  const state = customer.shippingAddress?.stateCode || customer.shippingAddress?.state
  const customerSalesforceId = customer?.salesforceId as string | undefined

  const validateState = country === Country.US ? Boolean(state) : true
  const isRecommendationQueryEnabled = Boolean(country) && validateState && Boolean(customerSalesforceId) && Boolean(type)

  useRedirectToConsultationSchedulingFlowIfNeeded({ appointmentType: type })

  const [
    availabilityWindowInDays,
    isSquadBasedSchedulingEnabled,
    allowChangingTeamAccessFeatureFlag,
    isRecommendationBasedAvailabilitiesEnabled,
  ] = useFeatureFlags([
    'SCHEDULING_AVAILABILITY_WINDOW_IN_DAYS',
    'ENABLE_SQUAD_BASED_SCHEDULING',
    'ALLOW_CHANGING_TEAM_ACCESS',
    'RECOMMENDATION_BASED_AVAILABILITIES',
  ])

  const filterBySalesTeamGid =
    isSquadBasedSchedulingEnabled && teamAccess === TeamAccess.MY_TEAM && teleaudiologyOrTaLightJourneys.has(journey)

  const { consultant, isLoading: isConsultantLoading } = useConsultantUser()
  const {
    recommendations,
    isError: haveProviderRecommendationsErrored,
    isLoading: areProviderRecommendationsLoading,
  } = useProviderRecommendations(
    {
      appointmentType: type,
      customerSalesforceId,
      country: country as string,
      state: state as string,
      journey,
      shopSalesforceId: restrictToShopSalesforceId,
      salesTeamGid: filterBySalesTeamGid ? consultant?.salesTeamGid : undefined,
    },
    isRecommendationQueryEnabled && (filterBySalesTeamGid ? !isConsultantLoading : true)
  )
  const { resource, isLoading: isManualTimeEnabledResourceLoading } = useManualTimeEnabledResource(journey, recommendations)

  // Create resources from recommendations for the dropdown
  const allResources: Resource[] = useMemo(
    () => (recommendations?.providers?.length ? recommendations.providers.map((provider) => toResource(provider.resource)) : []),
    [recommendations]
  )

  // Filter resources based on specialist selection for availability queries
  const filteredResources = useMemo(() => allResources.filter(filterByResource(specialistId)), [allResources, specialistId])

  const onResourceChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const selectedValue = event.target.value as string
      const isAnyoneSelected = isAnyResourceId(selectedValue)

      // Clear time selection when resource changes
      setValue('clientStartTime', null)
      setValue('start_date', null)
      // Placeholder: gets overwritten once a time slot is picked
      // (via determineResourceIds or eligibleResourcePairs → random selection at submit).
      setValue('resource_ids', isAnyoneSelected ? [ANY_RESOURCE_ID] : [])
    },
    [setValue]
  )

  useEffect(() => {
    const title = appointmentToTitle({ customer, type })

    setValue('title', title)
  }, [type, customer, setValue])

  // Load recommended availabilities
  const appointmentTypeConfiguration = appointmentTypes ? appointmentTypes[type as AppointmentType] : undefined
  const durationInMinutes = appointmentTypeConfiguration?.duration_in_minutes ?? 0

  const { data: now = new Date() } = useNow()
  const dateEnd = endOfMonth(monthDate)
  const {
    canShowMoreDates: canShowMoreAcfuDates,
    isAcfuEligible,
    onShowMoreDates: onShowMoreAcfuDates,
    windowInDays,
  } = useAcfuProgressiveDisclosure(availabilityWindowInDays as number)
  // The shared SCHEDULING_AVAILABILITY_WINDOW_IN_DAYS flag has always meant "windowInDays - 1" here
  // (maxDate is exclusive) — harmless at 62 days, but the ACFU default of 3 needs to mean 3
  // selectable days literally, so only the ACFU-eligible branch skips the -1.
  const dateLimit = useMemo(
    () => startOfDayTimezoned(addDays(now, isAcfuEligible ? windowInDays : windowInDays - 1), customer_timezone),
    [now, isAcfuEligible, windowInDays, customer_timezone]
  )

  const isAvailabilitiesQueryEnabled =
    !areProviderRecommendationsLoading &&
    !areAppointmentTypesLoading &&
    Boolean(recommendations?.providers?.length) &&
    Boolean(appointmentTypeConfiguration?.duration_in_minutes) &&
    Boolean(filteredResources.length)

  // Per-shop CABIN_ONLY: multi-shop and restrict_to_shop.
  // When restrict_to_shop is set, resources are from one shop;
  // we still fetch that shop's config so CABIN_ONLY shops skip fitter pairing.
  const { shopIdToIsCabinOnly, isLoading: isShopIdToIsCabinOnlyLoading } = useShopIdToIsCabinOnly({
    journey: journey,
    resources: filteredResources,
    isEnabled: isAvailabilitiesQueryEnabled,
  })

  // Calculate dateStart from monthDate. Ensure it's not before the minimum allowed date in UTC
  const dateStartCandidate = startOfDayTimezoned(monthDate || new Date(), customer_timezone)
  const minimumAllowedDateUtc = getMinimumAllowedDateUtc(now)
  const dateStart = dateStartCandidate.getTime() < minimumAllowedDateUtc.getTime() ? minimumAllowedDateUtc : dateStartCandidate

  const isSingleShopContext = Boolean(restrictToShopSalesforceId)

  const availabilityParams = {
    appointmentType: type,
    dateEnd: dateEnd.getTime() < dateLimit.getTime() ? dateEnd : dateLimit,
    dateStart,
    isEnabled: isAvailabilitiesQueryEnabled,
    durationInMinutes,
    journey: journey,
    resources: filteredResources,
    shopIdToIsCabinOnly,
    timeZone: customer_timezone,
    ...(isSingleShopContext ? { mode: AvailabilityMode.DEFAULT } : {}),
    ...(isRecommendationBasedAvailabilitiesEnabled && recommendations?.gid ? { prsRecommendationGid: recommendations.gid } : {}),
  }

  const {
    data: availabilities,
    isError: haveRecommendedAvailabilitiesErrored,
    isLoading: areRecommendedAvailabilitiesLoading,
    refetch: refetchAvailabilities,
  } = useCombinedResourceAvailabilities(availabilityParams)

  const { data: asegoAvailabilities, isLoading: isAsegoDaysLoading } = useAsegoAvailabilityDays({
    ...availabilityParams,
    isEnabled: availabilityParams.isEnabled && !isSingleShopContext,
  })

  const mergedAvailabilityData = mergeAvailabilityResponses(availabilities, asegoAvailabilities)

  const { mergedData: finalMergedAvailabilityData, isLoading: isAsegoTimesLoading } = useAsegoTimesIntegration({
    ...availabilityParams,
    clientStartDate,
    asegoAvailabilities,
    mergedAvailabilityData,
  })

  const mergedAvailabilities = mergeAvailabilities(finalMergedAvailabilityData)

  const mergedResourceHasCalendarIntegration = {
    ...availabilities?.resourceHasCalendarIntegration,
    ...asegoAvailabilities?.resourceHasCalendarIntegration,
  }

  // Create resource options for the Select dropdown (excluding CABIN type)
  let resourceOptions = allResources
    .filter(filterResource({ isCabinOnlyConfiguration: false, resourceHasCalendarIntegration: mergedResourceHasCalendarIntegration }))
    .map(({ id, name, type }) => ({
      group: t(resourceTypeToDisplayText[type]),
      name: name as string,
      value: id,
    }))
    .sort(sortByGroupingThenName)

  if (resourceOptions.length > 0) {
    const hasInternalProviders = allResources.some((r) => r.type === ResourceType.FITTER_INTERNAL)
    const hasExternalProviders = allResources.some((r) => r.type === ResourceType.FITTER)
    const hasBothProviderTypes = hasInternalProviders && hasExternalProviders

    const anyOptions = [
      { group: t('common.label.anyProvider'), name: t('common.label.anyone'), value: ANY_RESOURCE_ID },
      ...(hasBothProviderTypes
        ? [
            { group: t('common.label.anyProvider'), name: t('common.label.anyInternalProvider'), value: ANY_INTERNAL_RESOURCE_ID },
            { group: t('common.label.anyProvider'), name: t('common.label.anyExternalProvider'), value: ANY_EXTERNAL_RESOURCE_ID },
          ]
        : []),
    ]
    resourceOptions = [...anyOptions, ...resourceOptions]
  }

  if (areAppointmentTypesLoading) {
    return <Loading />
  }

  const customerFieldsIssues: ComponentProps<typeof Issues>['summary'] = {}

  if (!customer.shippingAddress?.country)
    customerFieldsIssues['customer.fields'] = [...(customerFieldsIssues['customer.fields'] ?? []), 'customer.shippingAddress.country']

  if (!customer.shippingAddress?.state)
    customerFieldsIssues['customer.fields'] = [...(customerFieldsIssues['customer.fields'] ?? []), 'customer.shippingAddress.state']

  if (!customer.salesforceId)
    customerFieldsIssues['customer.fields'] = [...(customerFieldsIssues['customer.fields'] ?? []), 'customer.salesforceId']

  if (Object.keys(customerFieldsIssues).length) return <Issues summary={customerFieldsIssues} />

  if (haveProviderRecommendationsErrored) {
    return <Issues summary={{ 'recommendation.issues': ['recommendation.issues.default'] }} />
  }

  if (!areProviderRecommendationsLoading) {
    const recommendationIssues = determineRecommendationIssues({ appointmentType: type, customer, recommendations })
    if (recommendationIssues?.summary && Object.keys(recommendationIssues.summary).length) {
      return <Issues summary={recommendationIssues.summary} summaryMetadata={recommendationIssues.metaData} />
    }
  }

  const areAvailabilitiesLoading =
    areProviderRecommendationsLoading ||
    areRecommendedAvailabilitiesLoading ||
    isAsegoDaysLoading ||
    isManualTimeEnabledResourceLoading ||
    isShopIdToIsCabinOnlyLoading

  // Group all time zones
  const ianaTimezones = createTimeZonesFromGroups(timeZones)
  const isProcessing = areAppointmentTypesLoading || areAvailabilitiesLoading || formState.isSubmitting

  const showTeamAccessDropdown =
    isSquadBasedSchedulingEnabled && allowChangingTeamAccessFeatureFlag && teleaudiologyOrTaLightJourneys.has(journey)

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4} minHeight="22rem">
        <Grid item xs={12} marginBottom={2}>
          <Typography color="primary" variant="h1">
            {customer.familyName}, {customer.givenName} ({customer.salutation})
          </Typography>
        </Grid>
        <AppointmentTypeSubstitutedAlert
          blockedAppointmentTypes={blockedAppointmentTypes}
          marginBottom={2}
          selectedAppointmentType={type}
          substitution={substitution}
        />
        <Grid item xs={12} marginBottom={2}>
          <Select disabled={isRescheduling} label={t('common.label.typeOfAppointment')} name="type" options={appointmentTypeOptions} />
        </Grid>
        <Grid item xs={12} marginBottom={2}>
          <Select label={t('common.label.customerTimezone')} name="customer_timezone" options={getTimeZoneOptions(ianaTimezones)} />
        </Grid>
        <Grid item xs={12} marginBottom={2}>
          <TextArea disabled fullWidth label={t('common.label.customerState')} name="state" variant="standard" />
        </Grid>
        {showTeamAccessDropdown && (
          <Grid item xs={12} marginBottom={2}>
            <Select
              label={t('common.label.teamAccess')}
              name="teamAccess"
              options={[
                { name: t('common.label.teamAccessOptions.myTeam'), value: TeamAccess.MY_TEAM },
                { name: t('common.label.teamAccessOptions.allTeams'), value: TeamAccess.ALL_TEAMS },
              ]}
            />
          </Grid>
        )}
        {country === Country.DE && teleaudiologyOrTaLightJourneys.has(journey) && (
          <Grid item xs={12} marginBottom={2}>
            <Select
              defaultValue=""
              disabled={!resourceOptions.length}
              label={t('common.label.specialistType.fitter.title')}
              name="specialist_id"
              onChange={onResourceChange}
              options={resourceOptions}
            />
          </Grid>
        )}
      </Grid>
      <ScheduleContent isLoading={areAvailabilitiesLoading}>
        {haveRecommendedAvailabilitiesErrored && (
          <Grid item xs={12}>
            <Alert
              action={
                <Button color="inherit" size="small" onClick={() => refetchAvailabilities()}>
                  {t('common.label.tryAgain')}
                </Button>
              }
              severity="error"
            >
              {t('common.message.availabilitiesLoadError')}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} md={4} minHeight="22rem">
          <Day maxDate={dateLimit} availabilities={mergedAvailabilities} />
          {canShowMoreAcfuDates && <AcfuShowMoreDatesButton onExpand={onShowMoreAcfuDates} />}
        </Grid>
        <Grid item xs={12} md={4} minHeight="22rem">
          <Time
            loading={isAsegoTimesLoading}
            resource={resource}
            durationInMinutes={durationInMinutes}
            availabilities={mergedAvailabilities}
            availabilityData={finalMergedAvailabilityData}
            trackingData={trackingData}
          />
        </Grid>
      </ScheduleContent>
      {isAcfuEligible && (
        <Grid item xs={12}>
          <AcfuExtendedWindowReason />
        </Grid>
      )}
      <Grid item xs={12} textAlign={'end'} minHeight="4rem">
        <ActionButton loading={isProcessing} />
      </Grid>
      {debug && <pre>{JSON.stringify(getValues(), null, 2)}</pre>}
    </Grid>
  )
}

export { ScheduleForm }
